import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { useOrg } from "@/contexts/OrgContext";
import WhatsappSetupWizard from "@/pages/WhatsappSetupWizard";
import {
  Building2, Users, MessageCircle, UserPlus, Check, ArrowRight, ArrowLeft,
  Upload, Loader2, Palette, Image as ImageIcon, Plus, X, Sparkles, ChevronRight,
} from "lucide-react";

const STEPS = [
  { key: "agency",    label: "Agência",    icon: Building2 },
  { key: "promoters", label: "Promotores", icon: Users },
  { key: "whatsapp",  label: "WhatsApp",   icon: MessageCircle },
  { key: "team",      label: "Equipa",     icon: UserPlus },
];

// Stable per-row key so removing a middle row doesn't shift React keys and
// cause input focus / state to jump between rows (fixes array-index-as-key).
let _rowKeySeq = 0;
const makeRowKey = () => `row-${Date.now()}-${_rowKeySeq++}`;

export default function Onboarding() {
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStep = Math.max(0, Math.min(STEPS.length - 1, parseInt(searchParams.get("step") || "1", 10) - 1));
  const [stepIdx, setStepIdx] = useState(initialStep);
  const [status, setStatus] = useState(null);

  const refreshStatus = () => api.get("/organizations/me/onboarding-status").then((r) => setStatus(r.data));

  useEffect(() => { refreshStatus().catch(() => {}); }, []);

  const finishOnboarding = async () => {
    await api.post("/organizations/me/onboarding/complete", {}).catch(() => {});
    navigate(tPath("/dashboard"));
  };

  const goTo = (idx) => {
    setStepIdx(idx);
    setSearchParams({ step: String(idx + 1) }, { replace: true });
  };

  const next = () => { if (stepIdx < STEPS.length - 1) goTo(stepIdx + 1); else finishOnboarding(); };
  const back = () => { if (stepIdx > 0) goTo(stepIdx - 1); };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="onboarding-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Configuração inicial</div>
              <h1 className="font-display text-lg font-bold text-zinc-950">Bem-vindo à PromoCrew</h1>
            </div>
          </div>
          <button
            data-testid="onboarding-skip-all"
            onClick={finishOnboarding}
            className="text-xs text-zinc-500 hover:text-zinc-900"
            title="Ir para o dashboard e continuar depois"
          >
            Fazer mais tarde
          </button>
        </div>

        {/* Stepper */}
        <div className="max-w-4xl mx-auto px-6 pb-4">
          <ol className="flex items-center gap-2 sm:gap-4 overflow-x-auto">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <li key={s.key} className="flex items-center gap-2 whitespace-nowrap">
                  <button
                    onClick={() => goTo(i)}
                    data-testid={`onboarding-step-tab-${s.key}`}
                    className={`inline-flex items-center gap-2 text-xs sm:text-sm font-medium ${
                      active ? "text-zinc-950" : done ? "text-emerald-700" : "text-zinc-400"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      active ? "bg-zinc-950 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"
                    }`}>
                      {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    </span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />}
                </li>
              );
            })}
          </ol>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {stepIdx === 0 && <AgencyStep onDone={next} onSkip={next} status={status} refreshStatus={refreshStatus} />}
        {stepIdx === 1 && <PromotersStep onDone={next} onSkip={next} onBack={back} refreshStatus={refreshStatus} />}
        {stepIdx === 2 && <WhatsappStep onDone={next} onSkip={next} onBack={back} refreshStatus={refreshStatus} />}
        {stepIdx === 3 && <TeamStep onFinish={finishOnboarding} onBack={back} refreshStatus={refreshStatus} />}
      </main>
    </div>
  );
}

// --------- Step 1: Agency ---------
function AgencyStep({ onDone, refreshStatus }) {
  const { org, refresh: refreshOrg } = useOrg();
  const [form, setForm] = useState({ name: "", primary_color: "#0a0a0a", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (org) setForm({
      name: org.name || "",
      primary_color: org.primary_color || "#0a0a0a",
      logo_url: org.logo_url || "",
    });
  }, [org]);

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800 * 1024) { setErr("Imagem demasiado grande (máx 800KB)."); return; }
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, logo_url: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.put("/organizations/me", form);
      await refreshOrg();
      await refreshStatus();
      onDone();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Erro a guardar.");
    } finally { setSaving(false); }
  };

  return (
    <Card title="Dados da agência" subtitle="Personaliza o nome e a identidade visual que os teus promotores vão ver.">
      <form onSubmit={submit} className="space-y-5" data-testid="onboarding-agency-form">
        <Field label="Nome da agência" required>
          <input
            data-testid="agency-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="form-input"
            placeholder="Ex: A tua agência"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Cor principal">
            <div className="flex items-center gap-3">
              <input
                type="color"
                data-testid="agency-color"
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-12 h-10 rounded-lg border border-zinc-200 cursor-pointer"
              />
              <input
                value={form.primary_color}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="form-input"
              />
            </div>
          </Field>
          <Field label="Logo (PNG/JPG, máx 800KB)">
            <div className="flex items-center gap-3">
              {form.logo_url ? (
                <img src={form.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-zinc-100 border border-zinc-200" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-400">
                  <ImageIcon className="w-4 h-4" />
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleLogo} data-testid="agency-logo" className="text-sm" />
              {form.logo_url && (
                <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-red-600 hover:underline">Remover</button>
              )}
            </div>
          </Field>
        </div>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{String(err)}</div>}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-100">
          <button type="submit" disabled={saving} data-testid="agency-continue" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Continuar <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>
    </Card>
  );
}

// --------- Step 2: Promoters ---------
function PromotersStep({ onDone, onSkip, onBack, refreshStatus }) {
  const [mode, setMode] = useState("manual"); // 'manual' | 'csv'
  const [rows, setRows] = useState([{ _key: makeRowKey(), name: "", phone: "", email: "" }]);
  const [csvText, setCsvText] = useState("");
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [err, setErr] = useState("");
  const [totalInOrg, setTotalInOrg] = useState(null);

  useEffect(() => {
    api.get("/promoters").then((r) => setTotalInOrg(r.data.length)).catch(() => {});
  }, [result]);

  const canContinue = (totalInOrg || 0) > 0;

  const addRow = () => setRows((r) => [...r, { _key: makeRowKey(), name: "", phone: "", email: "" }]);
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeRow = (i) => setRows((r) => r.length > 1 ? r.filter((_, idx) => idx !== i) : r);

  const parseCsv = (text) => {
    // Simple CSV parser: header (name, phone, email, location, tags) optional.
    // Supports comma or semicolon. Tags may be pipe-separated.
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) return [];
    const first = lines[0].toLowerCase();
    const hasHeader = /(name|nome)/.test(first) || /phone|telefone/.test(first) || /email/.test(first);
    const sep = first.includes(";") ? ";" : ",";
    let headers = ["name", "phone", "email", "location", "tags"];
    let dataLines = lines;
    if (hasHeader) {
      headers = lines[0].split(sep).map((h) => h.trim().toLowerCase().replace("nome", "name").replace("telefone", "phone").replace("localidade", "location"));
      dataLines = lines.slice(1);
    }
    return dataLines.map((l) => {
      const cols = l.split(sep).map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cols[i] ?? ""; });
      if (obj.tags && typeof obj.tags === "string") {
        obj.tags = obj.tags.split(/[|,]/).map((t) => t.trim()).filter(Boolean);
      } else {
        obj.tags = [];
      }
      return obj;
    });
  };

  const submit = async () => {
    setImporting(true); setErr(""); setResult(null);
    try {
      let payload = [];
      if (mode === "manual") {
        payload = rows.filter((r) => r.name.trim());
      } else {
        payload = parseCsv(csvText);
      }
      if (payload.length === 0) {
        setErr("Nenhuma linha válida para importar.");
        setImporting(false);
        return;
      }
      const { data } = await api.post("/promoters/bulk-import", { promoters: payload });
      setResult(data);
      await refreshStatus();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Falha ao importar.");
    } finally { setImporting(false); }
  };

  const csvExample = "nome,telefone,email,localidade,tags\nAna Silva,+351 912 000 000,ana@exemplo.pt,Lisboa,hostess|bilingue\nBruno Costa,+351 913 000 000,bruno@exemplo.pt,Porto,promotor";

  return (
    <Card title="Base de promotores" subtitle="Adiciona pelo menos alguns promotores. Podes sempre adicionar mais tarde — esta base é permanente e usada em todos os eventos.">
      <div className="space-y-5" data-testid="onboarding-promoters">
        {/* Mode toggle */}
        <div className="flex items-center gap-2 border border-zinc-200 rounded-lg p-1 bg-zinc-50 w-fit">
          <button
            onClick={() => setMode("manual")}
            data-testid="promoters-mode-manual"
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === "manual" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-600"}`}
          >Manual</button>
          <button
            onClick={() => setMode("csv")}
            data-testid="promoters-mode-csv"
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === "csv" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-600"}`}
          >CSV / Excel</button>
        </div>

        {mode === "manual" && (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r._key} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                <input placeholder="Nome" value={r.name} onChange={(e) => updateRow(i, { name: e.target.value })} className="form-input" data-testid={`row-name-${i}`} />
                <input placeholder="Telefone" value={r.phone} onChange={(e) => updateRow(i, { phone: e.target.value })} className="form-input" data-testid={`row-phone-${i}`} />
                <input placeholder="Email" value={r.email} onChange={(e) => updateRow(i, { email: e.target.value })} className="form-input" data-testid={`row-email-${i}`} />
                <button onClick={() => removeRow(i)} className="text-zinc-400 hover:text-red-600 p-2" title="Remover linha"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addRow} data-testid="promoters-add-row" className="inline-flex items-center gap-1 text-sm text-zinc-700 hover:text-zinc-950">
              <Plus className="w-3.5 h-3.5" /> Adicionar linha
            </button>
          </div>
        )}

        {mode === "csv" && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">Cola aqui o CSV (podes exportar do Excel/Google Sheets)</label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={8}
              placeholder={csvExample}
              data-testid="promoters-csv-textarea"
              className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-xs font-mono"
            />
            <div className="text-[11px] text-zinc-500">
              Colunas suportadas: <code>nome, telefone, email, localidade, tags</code>. Vírgula ou ponto-e-vírgula como separador. Tags separadas por <code>|</code>.
            </div>
          </div>
        )}

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{String(err)}</div>}

        {result && (
          <div data-testid="promoters-import-result" className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-900 space-y-1">
            <div><strong>{result.created}</strong> promotores criados · <strong>{result.duplicates}</strong> duplicados · <strong>{result.invalid}</strong> inválidos.</div>
            {totalInOrg !== null && <div className="text-xs">Total actual na agência: <strong>{totalInOrg}</strong> promotores.</div>}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-zinc-100">
          <button onClick={onBack} data-testid="promoters-back" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={importing} data-testid="promoters-import" className="inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Importar
            </button>
            <button
              onClick={onDone}
              disabled={!canContinue}
              data-testid="promoters-continue"
              className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              title={canContinue ? "" : "Importa ou adiciona pelo menos 1 promotor"}
            >
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={onSkip} data-testid="promoters-skip" className="text-xs text-zinc-500 hover:text-zinc-900">
              Fazer mais tarde
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// --------- Step 3: WhatsApp ---------
function WhatsappStep({ onDone, onSkip, onBack, refreshStatus }) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [waStatus, setWaStatus] = useState("not_configured");

  const load = () => api.get("/organizations/me/whatsapp-settings")
    .then((r) => setWaStatus(r.data?.active_status || r.data?.status || "not_configured"))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const isConnected = waStatus === "connected";

  return (
    <Card title="Ligar WhatsApp" subtitle="Liga o número WhatsApp Business que a tua agência utiliza. Podes fazer isto mais tarde — só é necessário quando quiseres enviar convites por WhatsApp.">
      <div className="space-y-5" data-testid="onboarding-whatsapp">
        <div className={`rounded-xl border p-5 ${isConnected ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-zinc-50/50"}`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isConnected ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 border border-emerald-200"}`}>
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-zinc-950">
                {isConnected ? "WhatsApp Business ligado" : "WhatsApp Business ainda não ligado"}
              </div>
              <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
                {isConnected
                  ? "Podes agora enviar convites reais aos teus promotores."
                  : "A PromoCrew usa a Cloud API oficial da Meta. Cada agência liga o seu próprio número — nunca partilhamos números entre agências."}
              </p>
              {!isConnected && (
                <button
                  onClick={() => setWizardOpen(true)}
                  data-testid="wa-open-wizard-onboarding"
                  className="mt-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                >
                  Ligar WhatsApp <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {wizardOpen && (
          <WhatsappSetupWizard
            onClose={() => setWizardOpen(false)}
            onDone={() => { setWizardOpen(false); load(); refreshStatus(); }}
          />
        )}

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-zinc-100">
          <button onClick={onBack} data-testid="wa-back" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onSkip} data-testid="wa-skip" className="text-sm text-zinc-600 hover:text-zinc-950">
              Configurar mais tarde
            </button>
            <button
              onClick={onDone}
              data-testid="wa-continue"
              className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold"
            >
              Continuar <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// --------- Step 4: Team ---------
function TeamStep({ onFinish, onBack, refreshStatus }) {
  const [invites, setInvites] = useState([{ _key: makeRowKey(), email: "", role: "gestor" }]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);
  const [err, setErr] = useState("");
  const orgId = useOrg().org?.id;

  const addRow = () => setInvites((r) => [...r, { _key: makeRowKey(), email: "", role: "gestor" }]);
  const updateRow = (i, patch) => setInvites((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeRow = (i) => setInvites((r) => r.length > 1 ? r.filter((_, idx) => idx !== i) : r);

  const send = async () => {
    if (!orgId) return;
    setSending(true); setErr(""); setResults([]);
    const out = [];
    for (const inv of invites) {
      if (!inv.email.trim()) continue;
      try {
        const { data } = await api.post(`/organizations/${orgId}/invitations`, { email: inv.email.trim(), role: inv.role });
        out.push({ email: inv.email, ok: true, dev_token: data?.dev_token });
      } catch (e) {
        out.push({ email: inv.email, ok: false, reason: formatApiError(e.response?.data?.detail) || "erro" });
      }
    }
    setResults(out);
    setSending(false);
    refreshStatus();
  };

  return (
    <Card title="Convidar equipa" subtitle="Convida gestores e viewers para colaborarem na agência. Podes deixar este passo em branco — os convites são enviados por email.">
      <div className="space-y-4" data-testid="onboarding-team">
        {invites.map((r, i) => (
          <div key={r._key} className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
            <input
              placeholder="email@agencia.pt"
              value={r.email}
              onChange={(e) => updateRow(i, { email: e.target.value })}
              className="form-input"
              data-testid={`team-email-${i}`}
            />
            <select value={r.role} onChange={(e) => updateRow(i, { role: e.target.value })} className="form-input" data-testid={`team-role-${i}`}>
              <option value="admin">Admin</option>
              <option value="gestor">Gestor</option>
              <option value="viewer">Viewer</option>
            </select>
            <button onClick={() => removeRow(i)} className="text-zinc-400 hover:text-red-600 p-2" title="Remover"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={addRow} data-testid="team-add-row" className="inline-flex items-center gap-1 text-sm text-zinc-700 hover:text-zinc-950">
          <Plus className="w-3.5 h-3.5" /> Adicionar outro convite
        </button>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

        {results.length > 0 && (
          <div data-testid="team-results" className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 text-sm space-y-1">
            {results.map((r) => (
              <div key={r.email} className={r.ok ? "text-emerald-800" : "text-red-700"}>
                {r.ok ? "✓" : "✗"} {r.email} {r.ok ? "— convite criado" : `— ${r.reason}`}
                {r.ok && r.dev_token && (
                  <div className="text-[11px] text-zinc-500 break-all">
                    Link: <code>{window.location.origin}/accept-invitation?token={r.dev_token}</code>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-zinc-100">
          <button onClick={onBack} data-testid="team-back" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className="flex items-center gap-2">
            <button onClick={send} disabled={sending} data-testid="team-send" className="inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enviar convites
            </button>
            <button onClick={onFinish} data-testid="team-finish" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
              Concluir <Check className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// --------- Shared UI ---------
function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm">
      <div className="mb-6">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">{title}</h2>
        {subtitle && <p className="text-sm text-zinc-600 mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
      </div>
      {children}
      <style>{`.form-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.5rem;border:1px solid #e4e4e7;font-size:0.875rem;background:#fff} .form-input:focus{outline:none;border-color:#09090b;box-shadow:0 0 0 2px #09090b}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">
        {label}{required && <span className="text-red-600"> *</span>}
      </label>
      {children}
    </div>
  );
}
